/**
 * Break Button Component — June 2026 premium redesign
 * ----------------------------------------------------
 * Navbar widget for employee break management.
 *
 * Modes (locked by BreakButton.capTooltip.test.js):
 *   loading → clocked-out → on-break → cap-reached → ready
 *
 * Ready mode renders a Popover panel with:
 *   • daily allowance progress bar (used / 90 min)
 *   • one card per break type with usage pips (per-day entitlement)
 *   • disabled cards with explicit blocked_reason
 * On-break mode renders a live pill with a second-precision timer and
 * an inline progress bar (emerald → amber → red as the limit nears).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { usePoller } from '../../hooks/usePoller';
import {
  Coffee,
  Square,
  AlertTriangle,
  Loader2,
  ChevronDown,
  Utensils,
  UserRound,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const TYPE_VISUALS = {
  lunch: { Icon: Utensils, iconColor: 'text-amber-600', iconBg: 'bg-amber-50' },
  short_break: { Icon: Coffee, iconColor: 'text-sky-600', iconBg: 'bg-sky-50' },
  personal: { Icon: UserRound, iconColor: 'text-violet-600', iconBg: 'bg-violet-50' },
};
const DEFAULT_VISUAL = { Icon: Coffee, iconColor: 'text-gray-600', iconBg: 'bg-gray-100' };

const formatTime = (minutes) => {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
};

const formatClock = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

/** Per-day entitlement pips — filled dot = a used slot */
const UsagePips = ({ used, max, typeId }) => (
  <div
    className="flex items-center gap-1"
    title={`${used} of ${max} used today`}
    data-testid={`break-usage-pips-${typeId}`}
  >
    {Array.from({ length: max }).map((_, i) => (
      <span
        key={i}
        className={`w-2 h-2 rounded-full transition-colors duration-300 ${
          i < used ? 'bg-gray-800' : 'bg-white ring-1 ring-inset ring-gray-300'
        }`}
      />
    ))}
  </div>
);

/** One break-type card inside the popover panel */
const BreakTypeCard = ({ type, onStart, starting }) => {
  const { Icon, iconColor, iconBg } = TYPE_VISUALS[type.id] || DEFAULT_VISUAL;
  const disabled = type.can_start === false || starting;

  return (
    <button
      type="button"
      onClick={() => !disabled && onStart(type)}
      disabled={disabled}
      data-testid={`break-type-card-${type.id}`}
      className={`w-full text-left rounded-lg border p-3 transition-all duration-150 ${
        disabled
          ? 'opacity-55 cursor-not-allowed bg-gray-50 border-gray-200'
          : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm hover:-translate-y-px active:translate-y-0'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center ${iconBg}`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 truncate">{type.name}</span>
            {type.is_paid ? (
              <Badge className="text-[10px] px-1.5 py-0 bg-green-100 text-green-700 border-green-200 hover:bg-green-100">
                Paid
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-gray-500 border-gray-300">
                Unpaid
              </Badge>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {type.max_duration_minutes} min
            {type.max_per_day ? ` · ${type.max_per_day}×/day` : ''}
          </p>
        </div>
        {type.max_per_day ? (
          <UsagePips used={type.used_count || 0} max={type.max_per_day} typeId={type.id} />
        ) : null}
      </div>
      {type.blocked_reason && (
        <p
          className="mt-1.5 text-[11px] font-medium text-amber-600"
          data-testid={`break-blocked-reason-${type.id}`}
        >
          {type.blocked_reason}
        </p>
      )}
    </button>
  );
};

const BreakButton = () => {
  const [breakStatus, setBreakStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  // Second-precision tick while a break is active (drives timer + bar)
  const [nowMs, setNowMs] = useState(Date.now());
  // PR 2 — Track per-break reminder dismissal so we don't re-fire
  // the toast on every 30s poll while waiting for the ack to land.
  const [ackedBreakId, setAckedBreakId] = useState(null);
  // AUTH-401-LABELLING (Jul 2026) — track consecutive auth failures on the
  // 30s status poll so an expired session is surfaced instead of silently
  // ignored (labelled 401s trigger the global logout interceptor; this is
  // defence-in-depth for proxies that strip headers).
  const auth401CountRef = useRef(0);

  const fetchBreakStatus = useCallback(async (signal) => {
    try {
      const response = await axios.get(`${API}/api/breaks/status`, { signal });
      auth401CountRef.current = 0;
      setBreakStatus(response.data);

      if (response.data.active_break) {
        // PR 2 — Server-driven reminder toast. Fire once when the
        // backend says reminder_pending=true, then immediately ack
        // so the flag clears on the next poll.
        const ab = response.data.active_break;
        if (ab.reminder_pending && ackedBreakId !== ab.id) {
          const lead = ab.reminder_lead_minutes || 5;
          toast.warning(`${ab.break_type_name || 'Break'} ending soon`, {
            description: `About ${lead} minute${lead === 1 ? '' : 's'} left before this break auto-ends.`,
            duration: 8000,
          });
          setAckedBreakId(ab.id);
          try {
            await axios.post(`${API}/api/breaks/reminder/ack`);
          } catch (e) {
            // Non-fatal: backend will re-surface on next poll.
            console.warn('Failed to ack break reminder:', e);
          }
        }
      } else if (ackedBreakId) {
        setAckedBreakId(null);
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        console.warn('Break status poll exceeded its 25s window and was cancelled — retrying with backoff');
        return false;
      }
      console.error('Error fetching break status:', err);
      if (err.response?.status === 401) {
        auth401CountRef.current += 1;
        if (auth401CountRef.current >= 2) {
          toast.error('Your session has expired', {
            id: 'break-session-expired',
            description: 'Please sign in again to continue. Your break state is saved on the server.',
            duration: 10000,
          });
        }
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [ackedBreakId]);

  useEffect(() => {
    fetchBreakStatus();
  }, [fetchBreakStatus]);

  // POLL-HERD-001: staggered, overlap-safe, backoff-aware polling.
  usePoller(fetchBreakStatus, 30000, { immediate: false });

  // 1s tick while on break for the live timer/progress bar
  const activeBreakId = breakStatus?.active_break?.id;
  useEffect(() => {
    if (!activeBreakId) return undefined;
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [activeBreakId]);

  const handleStartBreak = async (breakType) => {
    try {
      setStarting(true);
      await axios.post(`${API}/api/breaks/start`, { break_type_id: breakType.id });
      toast.success(
        `${breakType.name} break started (${breakType.max_duration_minutes} min max)`,
        { description: breakType.is_paid ? 'This is a paid break' : 'This is an unpaid break' }
      );
      setPopoverOpen(false);
      setNowMs(Date.now());
      fetchBreakStatus();
    } catch (err) {
      console.error('Error starting break:', err);
      toast.error(err.response?.data?.detail || 'Failed to start break');
    } finally {
      setStarting(false);
    }
  };

  const handleEndBreak = async () => {
    try {
      setEnding(true);
      const response = await axios.post(`${API}/api/breaks/end`);
      if (response.data.entitlement_waived) {
        toast.info(
          `Break ended (${response.data.duration_minutes} min) — not counted toward your daily allowance`,
          { description: 'Ended within the grace period, so your entitlement is still available.' }
        );
      } else if (response.data.flagged) {
        toast.warning(`Break ended (${response.data.duration_minutes} min) – ${response.data.flagged_reason}`);
      } else {
        toast.success(`Break ended (${response.data.duration_minutes} min)`);
      }
      fetchBreakStatus();
    } catch (err) {
      console.error('Error ending break:', err);
      if (err.response?.status === 401) {
        toast.error('Your session has expired', {
          id: 'break-session-expired',
          description: 'Please sign in again — your break will be ended once you are back in.',
          duration: 10000,
        });
      } else {
        toast.error(err.response?.data?.detail || 'Failed to end break');
      }
    } finally {
      setEnding(false);
    }
  };

  if (loading) {
    return (
      <div className="w-8 h-8 flex items-center justify-center">
        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
      </div>
    );
  }

  // Not clocked in - show disabled button with tooltip
  // NOTE: Disabled buttons swallow pointer events in Chromium, so the
  // <Button disabled> is wrapped in a span that takes over hover/focus
  // duties (canonical Radix workaround for tooltips on disabled triggers).
  if (!breakStatus?.is_clocked_in) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0} className="inline-flex" data-testid="break-not-clocked-in-trigger">
              <Button
                variant="ghost"
                size="sm"
                disabled
                className="gap-1 opacity-50 cursor-not-allowed pointer-events-none"
              >
                <Coffee className="w-4 h-4" />
                <span className="hidden sm:inline">Break</span>
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p className="text-xs font-medium">Clock in to start a break</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Currently on break — live pill with second-precision timer +
  // inline progress bar. Click to end.
  if (breakStatus?.active_break) {
    const activeBreak = breakStatus.active_break;
    const maxDuration = activeBreak.max_duration_minutes || 60;

    let elapsedSeconds = (activeBreak.elapsed_minutes || 0) * 60;
    if (activeBreak.start_time) {
      const started = new Date(activeBreak.start_time).getTime();
      if (!Number.isNaN(started)) {
        elapsedSeconds = Math.max(0, Math.floor((nowMs - started) / 1000));
      }
    }
    const elapsedMinutes = Math.floor(elapsedSeconds / 60);
    const isOverLimit = elapsedMinutes >= maxDuration;
    const isApproachingLimit = !isOverLimit && elapsedMinutes >= maxDuration - 5;
    const progressPct = Math.min(100, (elapsedSeconds / (maxDuration * 60)) * 100);

    const tone = isOverLimit
      ? {
          btn: 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100',
          bar: 'bg-red-500',
        }
      : isApproachingLimit
        ? {
            btn: 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100',
            bar: 'bg-amber-500',
          }
        : {
            btn: 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
            bar: 'bg-emerald-500',
          };

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={handleEndBreak}
              disabled={ending}
              className={`relative overflow-hidden gap-2 ${tone.btn}`}
              data-testid="end-break-btn"
            >
              {ending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isOverLimit ? (
                <AlertTriangle className="w-4 h-4" />
              ) : (
                <Square className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">
                On Break – {activeBreak.break_type_name}
              </span>
              <span className="sm:hidden">{activeBreak.break_type_name}</span>
              <span
                className="font-mono text-xs tabular-nums font-semibold"
                data-testid="break-elapsed-clock"
              >
                {formatClock(elapsedSeconds)}
              </span>
              <span
                className={`absolute bottom-0 left-0 h-[3px] ${tone.bar} transition-[width] duration-1000 ease-linear`}
                style={{ width: `${progressPct}%` }}
                data-testid="break-progress-bar"
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <div className="space-y-1">
              <p className="font-medium">Click to End Break</p>
              <p className="text-xs text-gray-400">
                Max: {formatTime(maxDuration)} | Remaining today: {formatTime(breakStatus.remaining_break_time)}
              </p>
              {activeBreak.is_paid === false && (
                <p className="text-xs text-orange-500">⚠️ This is an unpaid break</p>
              )}
              {isOverLimit && (
                <p className="text-xs text-red-500 font-medium">
                  ⚠️ Exceeded limit by {formatTime(elapsedMinutes - maxDuration)}
                </p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const remainingTime = breakStatus?.remaining_break_time || 0;
  const hasRemainingTime = remainingTime > 0;
  const totalUsedTime = breakStatus?.total_break_time_today || 0;
  const maxTotalPerDay = breakStatus?.max_total_per_day || 90;
  const breaksToday = breakStatus?.breaks_today?.length || 0;
  const usedPct = maxTotalPerDay > 0 ? Math.min(100, (totalUsedTime / maxTotalPerDay) * 100) : 100;

  // PR 3.1 — explicit cap-reached state with explanatory tooltip
  // (disabled button wrapped in a hoverable span — see PR 3.1.1).
  if (!hasRemainingTime) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0} className="inline-flex" data-testid="break-cap-reached-trigger">
              <Button
                variant="ghost"
                size="sm"
                disabled
                className="gap-1 opacity-50 cursor-not-allowed pointer-events-none"
                data-testid="break-cap-reached-btn"
              >
                <Coffee className="w-4 h-4" />
                <span className="hidden sm:inline">Break</span>
                <ChevronDown className="w-3 h-3" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <div className="space-y-1 max-w-[220px]">
              <p className="text-xs font-medium" data-testid="break-cap-tooltip-headline">
                No break time remaining today.
              </p>
              <p className="text-xs text-gray-300" data-testid="break-cap-tooltip-usage">
                {totalUsedTime} of {maxTotalPerDay} minutes used.
              </p>
              <p className="text-xs text-gray-400" data-testid="break-cap-tooltip-reset">
                Daily allowance resets at midnight ET.
              </p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Ready — premium popover panel with daily allowance + type cards
  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5"
          disabled={starting}
          data-testid="start-break-btn"
        >
          {starting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Coffee className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">Break</span>
          <ChevronDown className="w-3 h-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="break-popover-panel">
        {/* Header — daily allowance */}
        <div className="px-4 pt-4 pb-3">
          <div className="flex items-baseline justify-between">
            <p className="text-sm font-semibold text-gray-900">Take a break</p>
            <span
              className="text-xs font-semibold text-emerald-600"
              data-testid="break-remaining-label"
            >
              {formatTime(remainingTime)} left today
            </span>
          </div>
          <div
            className="mt-2 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden"
            data-testid="break-daily-progress"
          >
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                usedPct >= 90 ? 'bg-red-500' : usedPct >= 65 ? 'bg-amber-500' : 'bg-emerald-500'
              }`}
              style={{ width: `${usedPct}%` }}
            />
          </div>
          <p className="mt-1.5 text-[11px] text-gray-500">
            {totalUsedTime} of {maxTotalPerDay} min used
          </p>
        </div>

        {/* Break type cards */}
        <div className="border-t border-gray-100 p-2 space-y-1.5">
          {breakStatus?.break_types?.map((type) => (
            <BreakTypeCard
              key={type.id}
              type={type}
              onStart={handleStartBreak}
              starting={starting}
            />
          ))}
        </div>

        {/* Footer — today's summary + grace hint */}
        <div className="border-t border-gray-100 bg-gray-50/80 px-4 py-2 rounded-b-md">
          <p className="text-[11px] text-gray-500" data-testid="break-today-summary">
            <span className="font-medium text-gray-600">Today:</span>{' '}
            {breaksToday === 0 ? (
              <span className="text-gray-400">No breaks taken</span>
            ) : (
              <span>
                {breaksToday} break{breaksToday !== 1 ? 's' : ''}, {formatTime(totalUsedTime)} used
              </span>
            )}
          </p>
          {breakStatus?.grace_period_minutes > 0 && (() => {
            // Jun 14, 2026 — BREAK-VOID-001 UX clarification.
            // Backend caps grace voids at `max_grace_voids_per_day` per user
            // (default 2). Once that ceiling is hit, any further sub-grace-
            // window quick-end DOES consume entitlement. Previously the
            // popover only said "Breaks ended within 5m aren't counted"
            // which was technically correct but misleading when voids
            // were exhausted. We now swap the legend to a clear warning
            // so users understand the next quick break will count.
            const maxVoids = breakStatus.max_grace_voids_per_day || 0;
            const usedVoids = breakStatus.grace_voids_today || 0;
            const left = Math.max(0, maxVoids - usedVoids);
            if (left === 0 && maxVoids > 0) {
              return (
                <p
                  className="text-[10px] text-amber-700 font-medium mt-0.5 flex items-start gap-1"
                  data-testid="break-grace-exhausted-warning"
                >
                  <span aria-hidden="true">⚠</span>
                  <span>
                    Grace void limit reached for today. Future quick-ended
                    breaks will count toward your daily break allowance.
                  </span>
                </p>
              );
            }
            return (
              <p className="text-[10px] text-gray-400 mt-0.5" data-testid="break-grace-hint">
                Breaks ended within {breakStatus.grace_period_minutes}m aren't counted (
                {left} of {maxVoids} grace voids left today)
              </p>
            );
          })()}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default BreakButton;
